import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {apiRequest} from '../../utils/AxiosHelper';
import {useAppSelector} from '../hooks';
import API from '../../modules/settings/APIs';
import {LossEvent, LossEventStats} from '../../types/events';

export const eventQueryKeys = {
  all:    ['events'] as const,
  detail: (id: string | number) => ['events', 'detail', String(id)] as const,
  stats:  ['events', 'stats'] as const,
} as const;

export const useEventDetail = (id?: string | number) => {
  const {isAuthenticated, token} = useAppSelector(s => s.session);

  return useQuery<LossEvent>({
    queryKey: eventQueryKeys.detail(id || ''),
    queryFn: async () => {
      const response = await apiRequest.get(API.events.operations.get(id!));
      return response.data.data || response.data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!id && isAuthenticated && !!token,
  });
};

export const useEventStats = () => {
  const {isAuthenticated, token} = useAppSelector(s => s.session);

  return useQuery<LossEventStats>({
    queryKey: eventQueryKeys.stats,
    queryFn: async () => {
      const response = await apiRequest.get(API.events.operations.stats);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated && !!token,
  });
};

export const useCreateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest.post(API.events.operations.create, {data});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: eventQueryKeys.all});
    },
  });
};

export const useUpdateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({id, data}: {id: string | number; data: any}) => {
      const response = await apiRequest.post(API.events.operations.update, {id, data});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: eventQueryKeys.all});
    },
  });
};
